import {
  CALCULATION_CONTENT_CONTAINER_ID,
  VERSION_CONTENT_CONTAINER_ID,
} from '../../../shared/constants';
import { getNodeIgnoreFiberNodes, isAccessibleButton } from './nodes';

/**
 * Finds the first descendant of a node that is of type `TEXT_NODE`. Always selects the first child of a node (because we are looking for the start of the range).
 *
 * @param {Node} node - The node currently selected in the DOM tree.
 * @returns {Node | null} The first descendant of a node that is of type `TEXT_NODE` or has no more children.
 */
const getDownToStartTextNode = (node?: Node): Node | null => {
  if (!node) {
    return null;
  }

  if (node.nodeType === Node.TEXT_NODE || !node.firstChild) {
    return node;
  }

  return getDownToStartTextNode(node.firstChild);
};

/**
 * Finds the first descendant of a node that is of type `TEXT_NODE`. Always selects the last child of a node (because we are looking for the end of the range).
 *
 * @param {Node} node - The node currently selected in the DOM tree.
 * @returns {Node | null} The first descendant of a node that is of type `TEXT_NODE` or has no more children.
 */
const getDownToEndTextNode = (node: Node): Node => {
  if (node?.nodeType === Node.TEXT_NODE || !node?.lastChild) {
    return node;
  }

  return getDownToEndTextNode(node.lastChild);
};

/**
 * Finds the `TEXT_NODE` representing the end boundary point of the range.
 * Since the end boundary point for nodes of type `ELEMENT_NODE` is by default set at the beginning of the next container,
 * meaning no text in that container was selected, we need to traverse down the tree and also to the left.
 * Traversing left in the tree might require moving up first.
 *
 * @param {Node} node - The node currently representing the end boundary point.
 * @returns {Node | null} - The text node representing the end boundary point, or `null` if no suitable text node is found.
 */
const findEndContainer = (node?: Node): Node | null => {
  const versionContentContainer = document.getElementById(
    VERSION_CONTENT_CONTAINER_ID,
  );
  const calculationContentContainer = document.getElementById(
    CALCULATION_CONTENT_CONTAINER_ID,
  );

  if (!node) {
    return null;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    return node;
  }

  // stop before leaving the container of the content
  if (
    node === versionContentContainer ||
    node === calculationContentContainer
  ) {
    return null;
  }

  const parent = getNodeIgnoreFiberNodes(node, 'parentNode');
  const prevSibling = getNodeIgnoreFiberNodes(node, 'previousSibling');

  if (prevSibling) {
    return getDownToEndTextNode(prevSibling);
  }

  if (parent) {
    return findEndContainer(parent);
  }

  return null;
};

/**
 * Prepares the `Range` object for further calculations:
 * The container marking the start and end of a range can be HTML nodes of different types, e.g., `Node.TEXT_NODE` or `Node.ELEMENT_NODE`.
 * Depending on the `nodeType`, the offset either refers to a number of characters or a number of nodes.
 * To simplify further calculations, the range is altered to always have a text node as the start and end container.
 * Additionally, the range is supposed to be fully contained by the container that displays the version content.
 *
 * @param {Range} range - The `Range` object to process.
 * @param {Document} doc - The HTML document the `Range` object belongs to.
 * @returns {Range | undefined} - The processed `Range` object or `undefined` if a processing step failed.
 */
const preprocessRange = (range: Range, doc: Document): Range | undefined => {
  const versionContentContainer = doc.getElementById(
    VERSION_CONTENT_CONTAINER_ID,
  );
  const calculationContentContainer = doc.getElementById(
    CALCULATION_CONTENT_CONTAINER_ID,
  );

  if (
    range.startContainer === versionContentContainer ||
    range.startContainer === calculationContentContainer
  ) {
    try {
      const startContainer = range.startContainer.firstChild;
      if (startContainer) {
        range.setStart(startContainer, 0);
      }
    } catch {
      return undefined;
    }
  }

  if (
    range.endContainer === versionContentContainer ||
    range.endContainer === calculationContentContainer
  ) {
    try {
      const endContainer = range.endContainer.lastChild;
      const endOffset =
        endContainer?.nodeType === Node.TEXT_NODE
          ? endContainer.textContent?.length ?? 0
          : endContainer?.childNodes.length;
      if (endContainer && endOffset) {
        range.setEnd(endContainer, endOffset);
      }
    } catch {
      return undefined;
    }
  }

  // do not accept accessible link buttons as start or end container of range
  if (isAccessibleButton(range.startContainer)) {
    try {
      range.setStartAfter(range.startContainer);
      return preprocessRange(range, doc);
    } catch {
      return undefined;
    }
  }

  if (isAccessibleButton(range.endContainer)) {
    try {
      range.setEndBefore(range.endContainer);
      return preprocessRange(range, doc);
    } catch {
      return undefined;
    }
  }

  let newStartContainer: Node | null = null;
  let newEndContainer: Node | null = null;

  if (range.startContainer?.nodeType !== Node.TEXT_NODE) {
    newStartContainer = getDownToStartTextNode(
      range.startContainer.childNodes[range.startOffset],
    );
  }

  if (range.endContainer?.nodeType !== Node.TEXT_NODE) {
    newEndContainer = findEndContainer(
      range.endOffset === 0
        ? range.endContainer
        : range.endContainer.childNodes[range.endOffset - 1],
    );
  }

  try {
    if (newStartContainer) {
      range.setStart(newStartContainer, 0);
    }

    if (newEndContainer) {
      range.setEnd(newEndContainer, newEndContainer?.textContent?.length || 0);
    }
  } catch (error) {
    return undefined;
  }

  return range;
};

export default preprocessRange;
